import { useState, useRef, useEffect } from 'react'
import { ImagePlus, Trash2, X } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectPlanForm = ({ lightboxData, setLightboxData, onSave, setActiveTab, isProject }) => {
  const fileInputRef = useRef(null);
  const skipAutosave = useRef(false);
  const [draftId, setDraftId] = useState(null);
  // const [draftLoading, setDraftLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);

  const cardCls = "bg-white rounded-xl shadow p-8 mb-10";
  const labelCls = "block text-sm font-medium text-gray-700 mb-2";
  let saveTimer = useRef(null);
  const isInitialLoad = useRef(true);
  // ✅ Load existing draft on mount
  useEffect(() => {
    const loadDraft = async () => {
      const storedDraftId = localStorage.getItem("draftId");
      if (!storedDraftId) {
        isInitialLoad.current = false; // ✅ No draft → allow auto-save
        return;
      }

      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `${API_BASE_URL}/admin/get-draft-project/${storedDraftId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.draft) {
          setLightboxData(res.data.draft.draft_data);
          setDraftId(res.data.draft.draft_id);
        }
      } catch (err) {
        console.error("Error loading draft:", err);
      } finally {
        // ✅ Mark that initial load has completed
        isInitialLoad.current = false;
      }
    };

    if (!isProject) loadDraft();
    else isInitialLoad.current = false; // For new project form
  }, [isProject, API_BASE_URL, setLightboxData]);


  const autosaveDraft = async () => {
    if (!lightboxData.project_name || lightboxData.project_name.trim() === "") {
      return;
    }

    const token = localStorage.getItem("token");
    const payload = { draft_id: draftId, formData: lightboxData };

    const res = await axios.post(
      `${API_BASE_URL}/admin/save-draft-project`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!draftId && res.data.draft_id) {
      setDraftId(res.data.draft_id);
      localStorage.setItem("draftId", res.data.draft_id);
    }
  };

  useEffect(() => {
    if (!lightboxData || !lightboxData.project_name?.trim()) {
      return;
    }

    if (skipAutosave.current) {
      skipAutosave.current = false;
      return;
    }

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      autosaveDraft();
    }, 800);

    return () => clearTimeout(saveTimer.current);
  }, [lightboxData]);


  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append("media", file));

      const res = await axios.post(`${API_BASE_URL}/admin/upload-media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const uploadedUrls = res.data.data.files?.map(f => f.url) || [];

      setLightboxData(prev => ({
        ...prev,
        project_plan_images: [...(prev.project_plan_images || []), ...uploadedUrls]
      }));
    } finally {
      setUploadLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setLightboxData(prev => ({
      ...prev,
      project_plan_images: prev.project_plan_images.filter((_, i) => i !== index),
    }));
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };


  // if (draftLoading) {
  //   return <div className="p-10 text-center text-gray-600">Loading draft...</div>;
  // }
  const clearForm = () => {
    skipAutosave.current = true;   // ✅ Prevent autosave

    setLightboxData({
      ...lightboxData,
      project_plan_images: []
    });

    // ✅ After clearing, you may also want to remove draft
    localStorage.setItem("draftId", null);
    // setDraftId(null);
  };

  return (
    <section className={cardCls}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">Project Plan Images</h2>
        {/* <button
          onClick={onSave}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
        >
          <Save size={18} /> Save
        </button> */}
        <div className="flex items-center gap-3">
          {/* ✅ Clear Form Button */}
          <button
            onClick={clearForm}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
          >
            <X size={18} /> Clear
          </button>
          <button
            onClick={() => setActiveTab("location")}
            // disabled={!homeData || !onSave}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg"
          >
            Next
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {uploadLoading && (
          <div className="mt-2 w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto">
            please wait....
          </div>
        )}
        <div>
          <label className={labelCls}>Upload Plan Images</label>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-blue-400 transition"
            onClick={handleUploadClick}
          >
            <ImagePlus size={36} className="text-blue-500 mb-2" />
            <p className="text-gray-800 text-sm">Click to upload an Image</p>
            <p className="text-xs text-gray-600">Recommended image size: <strong>1600 × 1200 px</strong> (4:3), JPG/PNG, under 600 KB</p>
          </div>

          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            className="hidden"
            onChange={handleImageUpload}
          />


        </div>

        {lightboxData.project_plan_images?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-6">
            {lightboxData.project_plan_images.map((img, index) => (
              <div key={index} className="relative group border rounded-lg overflow-hidden">
                <img src={img} alt={`Plan ${index + 1}`} className="object-cover w-full h-32 sm:h-40" />
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProjectPlanForm;
